import { FC } from 'react';
import { Navigate } from 'react-router-dom';
import { ViewLoader } from '~/components';
import { withErrorBoundary } from '~/hocs';
import { useParams } from '~/hooks';
import { useCurrentWorkspace, useProcessesQuery } from '~/queries';
import { ApplicationLink } from '~/routes/link/Application';

/**
 * This is used as placeholder route to redirect user further down the path
 * 1. if user has assistantUser role for this workspace -> send to /link/assistant page
 * 2. if user has permissions to view processes and has zero processes configured send to welcome page
 * 3. if user has permissions to view processes or assistants -> send to activity page
 * 4. send to workspace settings page
 */
export const WorkspaceRoot: FC = withErrorBoundary(() => {
  const { workspaceShortName, organizationShortName } = useParams<
    'organizationShortName' | 'workspaceShortName'
  >();
  const { data: currentWorkspace, isFetching } = useCurrentWorkspace();
  const { data: processes = [], isLoading: isLoadingProcesses } = useProcessesQuery();

  if (isFetching || isLoadingProcesses) {
    return <ViewLoader key="root" />;
  }

  if (currentWorkspace?.roleName === 'assistantUser') {
    // Assistant User is a SSO role only, for which assistant uses the new authentication method only (credentials and hostname)
    return <ApplicationLink applicationId="assistant" type="credentials" hostname="default" />;
  }

  const canReadProcesses = currentWorkspace?.permissions?.includes('read_processes');
  if (canReadProcesses && processes.length === 0) {
    return <Navigate to={`/${organizationShortName}/${workspaceShortName}/processes`} />;
  }

  if (canReadProcesses) {
    return <Navigate to={`/${organizationShortName}/${workspaceShortName}/activity`} />;
  }

  const canReadAssistants = currentWorkspace?.permissions?.includes('read_assistants');
  if (canReadAssistants) {
    return (
      <Navigate to={`/${organizationShortName}/${workspaceShortName}/activity/assistantRuns`} />
    );
  }

  return <Navigate to={`/${organizationShortName}/${workspaceShortName}/settings`} />;
});
